import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  output,
  input,
  contentChildren,
} from '@angular/core';
import { Observable } from 'rxjs';
import { TableUIDataSource } from './entities/table-data-source.entity';
import { TableRowDefUIDirective } from './directives/table-row-def.directive';
import { TableHeaderRowDefUIDirective } from './directives/table-header-row-def.directive';
import { TableSkeletonRowUIComponent } from './components/table-skeleton-row/table-skeleton-row.component';
import { ContentBorderShadowUIComponent } from '../../atoms/content-border-shadow/content-border-shadow.component';
import { TableUIConfigType } from './models/table-config.model';
import { TableUIResizeResultType } from './models/table-resize.model';
import { TableUISortModel } from './models/table-sort.model';
import { TableUIPaging } from './models/table-paging.model';
import { TableConstructUIService } from './services/table-construct.service';
import { TableResizeUIService } from './services/table-resize.service';
import { TableSortUIService } from './services/table-sort.service';
import { UiInfiniteScrollDirective } from '../../../directives';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';

@Component({
  selector: 'fr8bot-ui-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  providers: [TableConstructUIService, TableResizeUIService, TableSortUIService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiInfiniteScrollDirective,
    NgTemplateOutlet,
    AsyncPipe,
    TableSkeletonRowUIComponent,
    ContentBorderShadowUIComponent,
  ],
})
export class TableUIComponent<DataType> {
  protected tableService = inject<TableConstructUIService<DataType>>(TableConstructUIService);
  protected resizeService = inject(TableResizeUIService);
  protected sortService = inject(TableSortUIService);

  readonly dataSource = input<TableUIDataSource<DataType> | null>(null);
  readonly config = input<TableUIConfigType | null>(null);
  readonly sortConfig = input<Partial<TableUISortModel> | null | undefined>(null);
  readonly isHeaderSticky = input(false);
  readonly loading = input(false);
  /** The first load: skeleton rows stand in for the data rows until it ends. */
  readonly skeleton = input(false);
  /** Without it the table shows what it has and requests nothing on scroll. */
  readonly paging = input<TableUIPaging | null>(null);

  readonly sortChange = output<TableUISortModel>();
  readonly resizeEnd = output<TableUIResizeResultType>();

  public data$: Observable<DataType[]> = this.tableService.data$;

  public readonly rows = contentChildren(TableRowDefUIDirective, { descendants: true });

  public readonly headerRows = contentChildren(TableHeaderRowDefUIDirective, { descendants: true });

  // Enough to fill a tall screen; the body clips the rows below the table's bottom edge.
  protected readonly skeletonRowSlots = Array.from({ length: 10 }, (_, index) => index);

  constructor() {
    // The services keep the last non-empty value, as the setters these replace did.
    effect(() => {
      const dataSource = this.dataSource();

      if (dataSource) {
        this.tableService.dataSource = dataSource;
      }
    });

    effect(() => {
      const config = this.config();

      if (config) {
        this.resizeService.setConfig(config);
      }
    });

    effect(() => {
      const sortConfig = this.sortConfig();

      if (sortConfig) {
        this.sortService.setConfig(sortConfig);
      }
    });

    effect(() => {
      const sortOrder = this.sortService.order();

      if (sortOrder) {
        this.sortChange.emit(sortOrder);
      }
    });

    effect(() => {
      const resizeEnd = this.resizeService.resizeEnd();
      if (resizeEnd) {
        this.resizeEnd.emit(resizeEnd);
      }
    });
  }
}
